/**
 * Runtime TimeScore computation.
 *
 * Pure functions that compute a daily score (0–100) from activities, goals, and
 * visions. No side effects, no API calls — all data is passed in.
 *
 * Formula (from docs/time-score.md §5):
 *   Daily Score = Execution (0–40) + Alignment (0–25) + Balance (0–20) + Consistency (0–15)
 */

import type { Activity, Goal, Vision } from '../api/client';
import { toLocalDateStr } from './format';

// ── Public types ────────────────────────────────────────────────────────────

export type DayType = 'normal' | 'light_day' | 'no_activity' | 'off_day';

export interface ScoreBreakdown {
  execution: {
    completedMinutes: number;
    plannedMinutes: number;
    completionRate: number;
    unplannedBonus: number;
    description: string;
  };
  alignment: {
    activityBreakdown: {
      goalAndVision: number;
      goalOnly: number;
      inactiveGoal: number;
      noGoal: number;
    };
    weightedAvg: number;
    description: string;
  };
  balance: {
    visionsTouched: number;
    totalActiveVisions: number;
    description: string;
  };
  consistency: {
    streakDays: number;
    eligibleDays: number;
    streakPts: number;
    trendRatio: number;
    trendPts: number;
    description: string;
  };
}

export interface ScoreResult {
  date: string; // YYYY-MM-DD
  total: number; // 0–100 (clamped, rounded)
  execution: number; // 0–40
  alignment: number; // 0–25
  balance: number; // 0–20
  consistency: number; // 0–15
  dayType: DayType;
  insight: string;
  breakdown: ScoreBreakdown;
}

// ── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_ESTIMATED_MINUTES = 60;
const LIGHT_DAY_MIN_THRESHOLD = 30;
const CONSISTENCY_WARM_START = 12;
const MAX_NEUTRAL_NO_ACTIVITY = 2;

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Parse encrypted estimated_minutes. Returns DEFAULT_ESTIMATED_MINUTES on failure. */
function parseEstimatedMinutes(encrypted: string | null): number {
  if (!encrypted) return DEFAULT_ESTIMATED_MINUTES;
  try {
    const decoded = atob(encrypted);
    const parsed = parseInt(decoded, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_ESTIMATED_MINUTES;
  } catch {
    return DEFAULT_ESTIMATED_MINUTES;
  }
}

/** Filter activities whose start_time falls on the given date (local timezone). */
function activitiesForDate(activities: Activity[], date: string): Activity[] {
  return activities.filter(
    (t) => toLocalDateStr(t.start_time) === date,
  );
}

/** Filter activities planned for a given date that are done or skipped. */
function completedActivitiesForDate(activities: Activity[], date: string): Activity[] {
  return activities.filter(
    (t) =>
      (t.status === 'done' || t.status === 'skipped') &&
      toLocalDateStr(t.start_time) === date,
  );
}

/** Build a goal-id → Goal lookup. */
function goalMap(goals: Goal[]): Map<string, Goal> {
  const m = new Map<string, Goal>();
  for (const g of goals) m.set(g.id, g);
  return m;
}

/** Build a goal-id → vision-id lookup. */
function goalVisionMap(goals: Goal[]): Map<string, string | null> {
  const m = new Map<string, string | null>();
  for (const g of goals) m.set(g.id, g.vision_id);
  return m;
}

// ── Day Type Classification ─────────────────────────────────────────────────

function classifyDay(
  plannedActivities: Activity[],
  completedActivities: Activity[],
  completedMinutes: number,
): DayType {
  const planned = plannedActivities.length;
  const completed = completedActivities.filter((t) => t.status === 'done').length;

  if (planned === 0 && completed === 0) return 'no_activity';
  if (
    (planned <= 1 && completed === 0) ||
    (completedMinutes < LIGHT_DAY_MIN_THRESHOLD && planned >= 2)
  ) {
    return 'light_day';
  }
  return 'normal';
}

// ── Component A: Execution (0–40) ───────────────────────────────────────────

function computeExecution(
  plannedActivities: Activity[],
  completedActivities: Activity[],
): { points: number; breakdown: ScoreBreakdown['execution'] } {
  const plannedMinutes = plannedActivities.reduce(
    (sum, t) => sum + parseEstimatedMinutes(t.estimated_minutes_encrypted),
    0,
  );
  const completedMinutes = completedActivities
    .filter((t) => t.status === 'done')
    .reduce((sum, t) => sum + parseEstimatedMinutes(t.estimated_minutes_encrypted), 0);

  if (plannedMinutes === 0) {
    return {
      points: 0,
      breakdown: {
        completedMinutes,
        plannedMinutes,
        completionRate: 0,
        unplannedBonus: 0,
        description: 'No activities planned',
      },
    };
  }

  const completionRate = Math.min(completedMinutes / plannedMinutes, 1);
  const basePoints = Math.sqrt(completionRate) * 40;

  // Unplanned bonus: activities completed today that weren't planned for today
  // Since we're computing based on start_time = date, "unplanned" activities are those
  // completed on the date but whose start_time is on a different day.
  // For simplicity in the runtime model, we skip unplanned bonus (all activities
  // shown on a day are planned for that day via start_time).
  const unplannedBonus = 0;

  const points = Math.min(basePoints + unplannedBonus, 44); // cap at 40 + bonus 4

  const doneCount = completedActivities.filter((t) => t.status === 'done').length;
  const description = `Completed ${doneCount} of ${plannedActivities.length} planned activities (${Math.round(completionRate * 100)}%)`;

  return {
    points: Math.min(points, 44),
    breakdown: {
      completedMinutes,
      plannedMinutes,
      completionRate,
      unplannedBonus,
      description,
    },
  };
}

// ── Component B: Alignment (0–25) ───────────────────────────────────────────

function computeAlignment(
  completedActivities: Activity[],
  goals: Goal[],
): { points: number; breakdown: ScoreBreakdown['alignment'] } {
  const doneActivities = completedActivities.filter((t) => t.status === 'done');
  if (doneActivities.length === 0) {
    return {
      points: 0,
      breakdown: {
        activityBreakdown: { goalAndVision: 0, goalOnly: 0, inactiveGoal: 0, noGoal: 0 },
        weightedAvg: 0,
        description: 'No completed activities',
      },
    };
  }

  const gMap = goalMap(goals);
  const gvMap = goalVisionMap(goals);

  let weightedSum = 0;
  let totalMinutes = 0;
  const counts = { goalAndVision: 0, goalOnly: 0, inactiveGoal: 0, noGoal: 0 };

  for (const t of doneActivities) {
    const minutes = parseEstimatedMinutes(t.estimated_minutes_encrypted);
    totalMinutes += minutes;

    if (!t.goal_id) {
      counts.noGoal++;
      weightedSum += 0.3 * minutes;
    } else {
      const goal = gMap.get(t.goal_id);
      if (!goal) {
        counts.noGoal++;
        weightedSum += 0.3 * minutes;
      } else if (goal.status !== 'active') {
        counts.inactiveGoal++;
        weightedSum += 0.5 * minutes;
      } else if (gvMap.get(t.goal_id)) {
        counts.goalAndVision++;
        weightedSum += 1.0 * minutes;
      } else {
        counts.goalOnly++;
        weightedSum += 0.7 * minutes;
      }
    }
  }

  const weightedAvg = totalMinutes > 0 ? weightedSum / totalMinutes : 0;
  const points = weightedAvg * 25;

  const total = doneActivities.length;
  const linked = counts.goalAndVision + counts.goalOnly;
  const description =
    linked === total
      ? `All ${total} activities linked to active goals`
      : linked > 0
        ? `${linked} of ${total} activities linked to goals`
        : 'No activities linked to goals';

  return {
    points,
    breakdown: {
      activityBreakdown: counts,
      weightedAvg,
      description,
    },
  };
}

// ── Component C: Balance (0–20) ─────────────────────────────────────────────

function computeBalance(
  completedActivities: Activity[],
  goals: Goal[],
  visions: Vision[],
): { points: number; breakdown: ScoreBreakdown['balance'] } {
  // API already filters out deleted visions, so all passed visions are active
  const totalActiveVisions = visions.length;

  if (totalActiveVisions === 0) {
    return {
      points: 10, // baseline for users without visions
      breakdown: {
        visionsTouched: 0,
        totalActiveVisions: 0,
        description: 'No visions set up (baseline score)',
      },
    };
  }

  if (totalActiveVisions <= 1) {
    return {
      points: 20, // full credit — balance not applicable with one vision
      breakdown: {
        visionsTouched: totalActiveVisions,
        totalActiveVisions,
        description: 'Single vision — full balance credit',
      },
    };
  }

  const gvMap = goalVisionMap(goals);
  const touchedVisions = new Set<string>();

  for (const t of completedActivities) {
    if (t.status !== 'done' || !t.goal_id) continue;
    const visionId = gvMap.get(t.goal_id);
    if (visionId) touchedVisions.add(visionId);
  }

  const visionsTouched = touchedVisions.size;
  const denominator = Math.min(totalActiveVisions, 3);
  const coverage = Math.min(visionsTouched / denominator, 1.0);
  const points = coverage * 20;

  const description = `Touched ${visionsTouched} of ${totalActiveVisions} visions`;

  return {
    points,
    breakdown: { visionsTouched, totalActiveVisions, description },
  };
}

// ── Component D: Consistency (0–15) ─────────────────────────────────────────

interface PastDayInfo {
  date: string;
  hasActivity: boolean;
  dayType: DayType;
  executionPts: number;
  alignmentPts: number;
  balancePts: number;
}

function computeConsistency(
  pastDays: PastDayInfo[],
  todayABC: number,
): { points: number; breakdown: ScoreBreakdown['consistency'] } {
  // Warm start for new users (< 3 days of history)
  if (pastDays.length < 3) {
    return {
      points: CONSISTENCY_WARM_START,
      breakdown: {
        streakDays: 0,
        eligibleDays: 0,
        streakPts: 0,
        trendRatio: 0,
        trendPts: 0,
        description: 'New user — warm start score',
      },
    };
  }

  const last7 = pastDays.slice(0, 7);

  // Streak calculation
  const offDays = last7.filter((d) => d.dayType === 'off_day').length;
  const noActivityDays = last7.filter((d) => d.dayType === 'no_activity').length;
  const neutralNoActivity = Math.min(noActivityDays, MAX_NEUTRAL_NO_ACTIVITY);

  let eligibleDays = last7.length - offDays - neutralNoActivity;
  eligibleDays = Math.max(eligibleDays, 1);

  const activeDays = last7.filter(
    (d) => d.hasActivity && d.dayType !== 'off_day',
  ).length;

  const streakPts = (activeDays / eligibleDays) * 8;

  // Trend: compare today's ABC to 7-day average ABC
  const abcValues = last7
    .filter((d) => d.dayType !== 'off_day' && d.dayType !== 'no_activity')
    .map((d) => d.executionPts + d.alignmentPts + d.balancePts);

  let trendPts = 3.5; // neutral default
  let trendRatio = 1.0;
  if (abcValues.length > 0) {
    const avgRecent = abcValues.reduce((a, b) => a + b, 0) / abcValues.length;
    if (avgRecent > 0) {
      trendRatio = Math.min(todayABC / avgRecent, 1.5);
      trendPts = (trendRatio / 1.5) * 7;
    }
  }

  const points = streakPts + trendPts;
  const description = `Active ${activeDays} of ${last7.length} days${
    trendRatio >= 1.0 ? ', trending up' : ''
  }`;

  return {
    points,
    breakdown: {
      streakDays: activeDays,
      eligibleDays,
      streakPts,
      trendRatio,
      trendPts,
      description,
    },
  };
}

// ── Insight Messages ────────────────────────────────────────────────────────

function generateInsight(
  total: number,
  dayType: DayType,
  breakdown: ScoreBreakdown,
): string {
  if (dayType === 'off_day') return 'Marked as off — excluded from trends.';
  if (dayType === 'no_activity') return 'No activity today.';
  if (dayType === 'light_day') return 'Light day — no score displayed.';

  // Find weakest component (relative to its max)
  const components = [
    { name: 'execution', ratio: breakdown.execution.completionRate },
    { name: 'alignment', ratio: breakdown.alignment.weightedAvg },
    {
      name: 'balance',
      ratio:
        breakdown.balance.totalActiveVisions > 1
          ? breakdown.balance.visionsTouched /
            Math.min(breakdown.balance.totalActiveVisions, 3)
          : 1,
    },
  ];
  const weakest = components.reduce((min, c) => (c.ratio < min.ratio ? c : min));

  if (total >= 90)
    return 'Outstanding day — you executed your plan and stayed balanced.';
  if (total >= 75)
    return 'Strong day! You completed most of what you planned.';
  if (total >= 55) {
    if (weakest.name === 'alignment')
      return 'Decent day. Linking activities to goals can boost your alignment score.';
    if (weakest.name === 'balance')
      return 'You focused on one area today. Spreading effort across your visions improves balance.';
    return 'Decent day. Keep it up!';
  }
  if (total >= 35) {
    if (weakest.name === 'execution')
      return 'Tough day. Consider planning fewer, higher-impact activities.';
    return "Tough day, but you still showed up. Tomorrow is a fresh start.";
  }
  return "Rest and reset. One day doesn't define your journey.";
}

// ── Main Entry Points ───────────────────────────────────────────────────────

/**
 * Compute the score for a single day.
 *
 * @param date - YYYY-MM-DD in local timezone
 * @param activities - All user activities (will be filtered to the date)
 * @param goals - All user goals
 * @param visions - All user visions
 * @param pastScores - Scores for previous days (most recent first), used for consistency.
 *                     Pass [] for the first day.
 */
export function computeScore(
  date: string,
  activities: Activity[],
  goals: Goal[],
  visions: Vision[],
  pastScores: ScoreResult[] = [],
): ScoreResult {
  const planned = activitiesForDate(activities, date);
  const completed = completedActivitiesForDate(activities, date);

  const exec = computeExecution(planned, completed);
  const align = computeAlignment(completed, goals);
  const bal = computeBalance(completed, goals, visions);

  const completedMinutes = completed
    .filter((t) => t.status === 'done')
    .reduce((s, t) => s + parseEstimatedMinutes(t.estimated_minutes_encrypted), 0);

  const dayType = classifyDay(planned, completed, completedMinutes);

  // Build past-day info from pastScores for consistency calculation
  const pastDays: PastDayInfo[] = pastScores.map((s) => ({
    date: s.date,
    hasActivity: s.dayType === 'normal' || s.dayType === 'light_day',
    dayType: s.dayType,
    executionPts: s.execution,
    alignmentPts: s.alignment,
    balancePts: s.balance,
  }));

  const todayABC = exec.points + align.points + bal.points;
  const consist = computeConsistency(pastDays, todayABC);

  const rawTotal = exec.points + align.points + bal.points + consist.points;
  const total = Math.max(0, Math.min(100, Math.round(rawTotal)));

  const breakdown: ScoreBreakdown = {
    execution: exec.breakdown,
    alignment: align.breakdown,
    balance: bal.breakdown,
    consistency: consist.breakdown,
  };

  const insight = generateInsight(total, dayType, breakdown);

  return {
    date,
    total,
    execution: Math.round(exec.points * 10) / 10,
    alignment: Math.round(align.points * 10) / 10,
    balance: Math.round(bal.points * 10) / 10,
    consistency: Math.round(consist.points * 10) / 10,
    dayType,
    insight,
    breakdown,
  };
}

/**
 * Compute scores for the last 7 days (for the sparkline).
 *
 * Returns results ordered oldest → newest (index 0 = 7 days ago).
 */
export function computeWeekScores(
  today: string,
  activities: Activity[],
  goals: Goal[],
  visions: Vision[],
): ScoreResult[] {
  const results: ScoreResult[] = [];

  // Compute from 7 days ago to today
  for (let i = 7; i >= 0; i--) {
    const d = new Date(today + 'T12:00:00'); // noon to avoid DST issues
    d.setDate(d.getDate() - i);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const score = computeScore(dateStr, activities, goals, visions, results);
    results.push(score);
  }

  return results;
}
